import React from "react";
import Maths from "oc-commons/Maths";
import Commons from "oc-commons/Commons";
import Doms from "oc-commons/Doms";

/**
 * <NumberInput label={I18N("Raum Höhe")}
                value={APP.arrangement.raum.size.y}
                onCommit={value=>APP.arrangement.setRaumHoehe(value)}
                unit={APP.UNIT}
                autoFocus={true} />
 *
 * Achtung, statt onBlur sollte onCommit verwendet werden => dann wird wirklich nur bei einer echten
 * Änderung der Listener aufgerufen
 *
 * Die Props sind:
 *
 * value: der angezeigte Wert
 *
 * defaultValue: der Vorgabewert
 *
 * autoFocus: bekommt das Input feld automatisch den Focus?
 *
 * onCommit: callback für ENTER-Taste oder Focus-Lost, bessere Alternative zu onChange
 *
 * onBlur: callback für blur event (focus lost)
 *
 * onChange: callback für onChange Event
 *
 * onFocus: callback für onFocus Event
 *
 * unit: ein String wie "cm"
 *
 * unitFactor: der Wert wird für die Anzeige mit unitFactor multipliziert. Wenn also unit "cm" aber
 * der Wert in "m" ist, wäre unitFactor=100
 *
 * decimals: wie viele Nachkommastellen werden fix angezeigt
 *
 * min,max: möglicher Minimal- und Maximalwert step: Bei Klick auf die + / - Buttons wird
 * um den Wert step hoch / runtergezählt
 *
 * minusFirst: bei true - / + sonst + / - als Button-Reihenfolge
 *
 * disabled: bei true wird das NumberInput ausgegraut und inaktiv
 *
 * isValid: Callback, das den aktuellen Wert entgegennimmt und true/false zurück gibt. Bei False wird der
 * Wert nicht akzeptiert und das Feld rot angezeigt
 *
 * className: string, zusätzliche css-Klassen
 *
 * label: Text des optionalen Labels id: optionales id Attributes name: optionales name Attribut
 *
 * autoSize: bei true passt sich die Width des Input-Feldes an die Länge der Eingabe an
 *
 * relaxedParsing: Eingaben in englischer und deutscher Notation werden akzeptiert.
 *
 */
export default class NumberInput extends React.Component {

    ref = React.createRef();
    state = {
        blurred: true,
        valueBeforeEdit: null,
    };

    componentDidMount() {
        const initialValue = this.props.defaultValue !== undefined ? this.props.defaultValue : this.props.value;
        this.setInputNumber(initialValue);
        const input = $(this.ref.current);
        // Reacts onChange == onInput, daher hier zu Fuß
        // https://stackoverflow.com/questions/38256332/in-react-whats-the-difference-between-onchange-and-oninput
        input.on('change', () => this.onChange());
        input.on('blur', () => this.onBlur());
        input.on('keydown', event => {
            if (event.keyCode === 13 /*ENTER*/) {
                event.preventDefault();
                this.doCommit();
            }
        });
        if (this.props.autoSize) {
            input.on('keypress', this.maybeAutoSize);
        }
        if (this.props.autoFocus) {
            input.focus();
        }
    }

    componentWillUnmount() {
        const input = $(this.ref.current);
        input.off('change');
        input.off('blur');
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.defaultValue === undefined && (this.state.blurred || nextProps.value !== this.props.value)) {
            this.setInputNumber(nextProps.value);
        }
        if (this.state.blurred) {
            this.setState({blurred: false});
        }
    }

    onBlur() {
        this.setState({blurred: true});
        this.props.onBlur && this.props.onBlur(this.getInputNumber());
        this.hasNewValue() && this.doCommit();
    }

    hasNewValue() {
        return this.formatFloat(this.getInputNumber()) != this.formatFloat(this.state.valueBeforeEdit);
    }

    doCommit() {
        this.props.onCommit && this.props.onCommit(this.getInputNumber());
        this.maybeAutoSize();
    }

    onChange() {
        return this.props.onChange && this.props.onChange(this.getInputNumber());
    }

    onFocus(e) {
        if (this.props.autoSelect) {
            e.persist();
            _.delay(() => e.target.select(), 100);
        }
        return this.props.onFocus && this.props.onFocus(e);
    }

    onKeyDown(event) {
        const keycode = (event.keyCode ? event.keyCode : event.which);
        if (keycode == 13) {
            this.props.onBlur && this.props.onBlur(this.getInputNumber());
        }
    }

    setInputNumber(number) {
        const element = $(this.ref.current);
        const value = this.formatFloat(number);
        element.val(value);
        this.maybeAutoSize();
        this.setState({...this.state, valueBeforeEdit: number});
    }

    maybeAutoSize = _.debounce(()=>{
        if (!this.props.autoSize || this.ref.current==null) {
            return;
        }
        const element = $(this.ref.current);
        const value = element.val();
        const width = Math.round(Doms.measureTextWidth(element, value)) + this.getPaddingWidth();
        this.ref.current.style.width = width + 'px';
    },200);

    getPaddingWidth() {
        if (this._paddingWidth!=null) {
            return this._paddingWidth;
        }
        const element = $(this.ref.current);
        return ['border-left-width','border-right-width','padding-left','padding-right'].reduce((acc,v)=>{
            const w = element.css(v);
            const p = parseInt(w,10);
            return acc+(p||0);
        },0);
    }

    getInputNumber() {
        let numberString = $(this.ref.current).val();
        if (this.props.relaxedParsing) {
            // Die engl. Notation wird per regexp erkannt und in die Deutsche umgewandelt. Bei den zwei zweideutigen Fällen:
            // z.B. die Zahl 1.00000 wird als 1,00000 intepretiert und die Zahl 1,466 als 1,466.
            const result = numberString.match(/^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:(\.)\d+)?$/);
            if (result && result[1] === '.') {
                // Umwandlung im Falle Zahl mit engl. Notation, z.B. 2,123.6
                if (numberString.includes(',')) {
                    numberString = numberString.replace(/\./, ',');
                    numberString = numberString.replace(/,/, '.');
                } else {
                    // Umwandlung von Zahl mit Punkt in DE Notation, z.B. 44.3 = 44,3
                    numberString = numberString.replace(/\./, ',');
                }
            }
        }
        return this.parseFloat(numberString);
    }

    parseFloat(text) {
        return this.round((Commons.isEmpty(text) ? 0 : I18N.parseFloat(text)) / this.getUnitFactor());
    }

    formatFloat(number) {
        return I18N.formatFloat((Maths.notFinite(number) ? 0 : number) * this.getUnitFactor(), this.getDecimals());
    }

    getUnitFactor() {
        return this.props.unitFactor || 1;
    }

    getUnit() {
        return this.props.unit;
    }

    getDecimals() {
        return this.props.decimals;
    }

    round(number) {
        let n = number;
        if (this.getDecimals() >= 0) {
            const p = Math.pow(10, this.getDecimals()) * this.getUnitFactor();
            n = Math.round(n * p) / p;
        }
        if (Maths.notFinite(n)) {
            n = 0;
        }
        if (this.props.min != undefined) {
            n = Math.max(n, this.props.min);
        }
        if (this.props.max != undefined) {
            n = Math.min(n, this.props.max);
        }
        return n;
    }

    step() {
        return this.props.step != null ? this.props.step : 1;
    }

    inc(step) {
        if (this.props.disabled) {
            return;
        }
        const newValue = this.round(this.getInputNumber() + step * this.step());
        if (this.isValid(newValue)) {
            this.setInputNumber(newValue);
            this.onChange();
            const input = $(this.ref.current);
            // für Dialoge zusäzlich über DOM triggern
            input.trigger('blur');
            this.doCommit();
        }
    }

    isValid(value) {
        return this.props.isValid ? this.props.isValid(value) : true;
    }

    classNameGroup() {
        return `input-group ${this.props.className || ''} ${this.props.hidden ? "disappear" : "appear"}`;
    }

    classNameInput() {
        return `form-control ui-number-input ${this.isValid(this.getInputNumber()) ? '' : 'invalid'}`
    }

    classNameUnit() {
        return 'unit';
    }

    classNameStep() {
        return `input-group-addon number-ui ${this.props.disabled ? 'disabled' : ''}`
    }

    render() {
        const props = this.props;
        const minusStep = <span className={this.classNameStep()}
                                onClick={() => this.inc(-1)}>-</span>;

        return (
            <div>
                {props.label && <label htmlFor={props.name}>{props.label}</label>}
                <div className={this.classNameGroup()}>
                    {props.addon && <span className="input-group-addon">{props.addon}</span>}


                    <input ref={this.ref}
                           id={props.id}
                           name={props.name}
                           type="text"
                           step={'' + this.step()}
                           className={this.classNameInput()}
                           disabled={props.disabled}
                           onFocus={(e) => this.onFocus(e)}
                           onKeyDown={(e) => this.onKeyDown(e)}
                           autoFocus={props.autoFocus} />

                    {this.getUnit() != null &&
                    <span className={this.classNameUnit()}>{this.getUnit()}</span>}

                    {this.props.step != null && <React.Fragment>
                        {this.props.minusFirst ? minusStep : null}
                        <span className={this.classNameStep()} onClick={() => this.inc(+1)}>+</span>
                        {this.props.minusFirst ? null : minusStep}
                    </React.Fragment>}

                </div>
            </div>
        );
    }

}
